import React, { useEffect, useMemo, useState } from "react";
import {
  Wand2, Loader2, Image as ImageIcon, Download, Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ChipPicker from "@/components/ChipPicker";
import ImageCard from "@/components/ImageCard";
import FolderPicker from "@/components/FolderPicker";
import Lightbox from "@/components/Lightbox";

const MAX_KEYWORDS = 5;
const CONCURRENCY = 3;

export default function Generator() {
  const [styles, setStyles] = useState([]);
  const [demographics, setDemographics] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [ctaColors, setCtaColors] = useState([]);
  const [keywordColors, setKeywordColors] = useState([]);
  const [costPerImage, setCostPerImage] = useState(0.039);

  const [keywordsText, setKeywordsText] = useState("");
  const [headline, setHeadline] = useState("");
  const [cta, setCta] = useState("");
  const [creativeDirection, setCreativeDirection] = useState("");

  // Selections — null/empty means "Auto / Smart Mode decides"
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedDemographics, setSelectedDemographics] = useState([]);
  const [selectedEthnicities, setSelectedEthnicities] = useState([]);
  const [language, setLanguage] = useState("auto");
  const [ctaColor, setCtaColor] = useState(null);       // null = Auto
  const [keywordColor, setKeywordColor] = useState(null); // null = Auto
  const [variations, setVariations] = useState(3);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [results, setResults] = useState([]);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [viewImage, setViewImage] = useState(null);

  useEffect(() => {
    api.get("/styles").then((r) => {
      setStyles(r.data.styles || []);
      setDemographics(r.data.demographics || []);
      setEthnicities(r.data.ethnicities || []);
      setLanguages(r.data.languages || []);
      setCtaColors(r.data.cta_colors || []);
      setKeywordColors(r.data.keyword_colors || []);
      setCostPerImage(r.data.cost_per_image_usd || 0.039);
    });
  }, []);

  const keywords = useMemo(
    () => keywordsText.split("\n").map((k) => k.trim()).filter(Boolean).slice(0, MAX_KEYWORDS),
    [keywordsText]
  );
  const styleCount = Math.max(selectedStyles.length, 1);  // empty = auto-pick UGC
  const demoMult = Math.max(selectedDemographics.length, 1);
  const ethMult = Math.max(selectedEthnicities.length, 1);
  const totalImages = keywords.length * styleCount * variations * demoMult * ethMult;
  const estimatedCost = (totalImages * costPerImage).toFixed(2);

  // Count overrides for advanced section header
  const overrideCount = [
    selectedStyles.length > 0,
    selectedDemographics.length > 0,
    selectedEthnicities.length > 0,
    language !== "auto",
    keywordColor !== null,
    ctaColor !== null,
  ].filter(Boolean).length;

  const toggle = (setter) => (s) =>
    setter((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const autoSuggest = async () => {
    if (keywords.length === 0) return toast.error("Add at least one keyword first");
    setSuggesting(true);
    try {
      const { data } = await api.post("/suggest", { keywords, language: language === "auto" ? null : language });
      if (data.style) setSelectedStyles([data.style]);
      if (data.demographic) setSelectedDemographics([data.demographic]);
      if (data.ethnicity) setSelectedEthnicities([data.ethnicity]);
      if (data.creative_direction) setCreativeDirection(data.creative_direction);
      setAdvancedOpen(true);
      toast.success(`Suggested: ${data.demographic} · ${data.ethnicity} · ${data.style}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-suggest failed");
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    if (keywords.length === 0) return toast.error("Add at least one keyword");

    // 1. Smart-Mode: classify keywords if any color is on Auto
    let verticalMap = {};
    if (ctaColor === null || keywordColor === null) {
      try {
        const { data } = await api.post("/classify", { keywords });
        verticalMap = data.classifications || {};
      } catch {
        // non-fatal — backend will fall back to per-call classification
      }
    }

    // 2. Build job list
    const stylesToUse = selectedStyles.length ? selectedStyles : ["UGC Caught in the Moment"];
    const demos = selectedDemographics.length ? selectedDemographics : [null];
    const eths = selectedEthnicities.length ? selectedEthnicities : [null];

    const jobs = [];
    for (const kw of keywords) {
      for (const st of stylesToUse) {
        for (const dm of demos) {
          for (const et of eths) {
            for (let v = 1; v <= variations; v++) {
              jobs.push({
                key: `${kw}__${st}__${dm || "any"}__${et || "any"}__${v}__${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                keyword: kw, style: st, demographic: dm, ethnicity: et, variation_number: v,
                vertical: verticalMap[kw] || null,
              });
            }
          }
        }
      }
    }
    setResults(jobs.map((j) => ({ ...j, status: "pending" })));
    setBusy(true);

    let cursor = 0;
    const total = jobs.length;
    const updateResult = (key, patch) =>
      setResults((cur) => cur.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    const worker = async () => {
      while (cursor < total) {
        const i = cursor++;
        const job = jobs[i];
        try {
          const { data } = await api.post("/generate", {
            keyword: job.keyword,
            style: job.style,
            variation_number: job.variation_number,
            demographic: job.demographic,
            ethnicity: job.ethnicity,
            language: language === "auto" ? null : language,
            cta_color: ctaColor,
            keyword_color: keywordColor,
            vertical: job.vertical,
            headline,
            cta,
            creative_direction: creativeDirection,
          });
          updateResult(job.key, { status: "ok", image: data });
        } catch (err) {
          updateResult(job.key, {
            status: "error",
            image: { error: err?.response?.data?.detail || err.message },
          });
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, total) }, worker);
    await Promise.all(workers);
    setBusy(false);
    toast.success(`Generated ${total} creative${total === 1 ? "" : "s"}`);
  };

  const downloadOne = async (img) => {
    try {
      const resp = await fetch(fileUrl(img.url));
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = img.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const downloadAll = async () => {
    const ok = results.filter((r) => r.status === "ok");
    if (ok.length === 0) return toast.error("Nothing to download");
    try {
      const resp = await api.post("/images/zip", { ids: ok.map((r) => r.image.id) }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `creatives-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("ZIP download failed"); }
  };

  const onMove = (img) => { setMoveTarget(img); setShowFolderPicker(true); };

  const moveTo = async (folderId) => {
    try {
      await api.patch(`/images/${moveTarget.id}/move`, { folder_id: folderId });
      toast.success(folderId ? "Moved to folder" : "Moved to Unfiled");
      setShowFolderPicker(false); setMoveTarget(null);
    } catch { toast.error("Move failed"); }
  };

  const onDelete = async (img) => {
    if (!confirm("Delete this image?")) return;
    try {
      await api.delete(`/images/${img.id}`);
      setResults((cur) => cur.filter((r) => r.image?.id !== img.id));
      setViewImage(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of results) {
      if (!map.has(r.keyword)) map.set(r.keyword, []);
      map.get(r.keyword).push(r);
    }
    return Array.from(map.entries());
  }, [results]);

  const completed = results.filter((r) => r.status !== "pending").length;
  const okResults = results.filter((r) => r.status === "ok");

  return (
    <div className="flex h-full" data-testid="generator-page">
      {/* Left controls panel */}
      <section className="w-full lg:w-[440px] border-r border-zinc-200 overflow-y-auto bg-white">
        <div className="p-6 lg:p-8 space-y-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Workspace</div>
            <h1 className="font-heading text-2xl font-bold text-zinc-900 mt-1">Generator</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Smart Mode is on — paste keywords, hit Generate. Colors and layout are picked automatically.
            </p>
          </div>

          {/* Smart Mode banner */}
          <div className="rounded-sm border border-[#0055FF]/15 bg-[#0055FF]/5 px-3 py-2.5 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-[#0055FF] flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="text-xs leading-relaxed">
              <span className="font-semibold text-[#0055FF]">Smart Mode</span>
              <span className="text-zinc-600"> classifies your keywords and picks the right color scheme per vertical (security → yellow, software → blue, food → orange, etc.) — and rotates colors across variations.</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-medium text-zinc-700">
                Keywords <span className="font-mono text-xs text-zinc-400 ml-1">{keywords.length}/{MAX_KEYWORDS}</span>
              </Label>
              <button
                onClick={autoSuggest}
                disabled={suggesting || keywords.length === 0}
                className="text-xs font-medium text-[#0055FF] hover:text-[#0040CC] disabled:text-zinc-300 flex items-center gap-1"
                data-testid="auto-suggest-button"
              >
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Auto-suggest
              </button>
            </div>
            <Textarea
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder={`one keyword per line\nMax ${MAX_KEYWORDS} keywords`}
              className="rounded-sm border-zinc-200 font-mono text-sm min-h-[120px]"
              data-testid="keywords-textarea"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-zinc-700">Variations per keyword</Label>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n} type="button" onClick={() => setVariations(n)}
                  className={`w-10 h-10 rounded-sm border text-sm font-mono ${
                    variations === n
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`variations-${n}`}
                >{n}</button>
              ))}
            </div>
            <p className="mt-1 text-xs text-zinc-400">Each variation gets a different on-vertical color scheme.</p>
          </div>

          {/* Cost summary */}
          <div className="border-t border-zinc-100 pt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Total images</span>
              <span className="font-mono font-semibold text-zinc-900" data-testid="total-images">{totalImages}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-500">Estimated cost</span>
              <span className="font-mono text-2xl font-bold text-zinc-900" data-testid="estimated-cost">${estimatedCost}</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              ${costPerImage.toFixed(3)} / image · Gemini 2.5 Flash Image
            </div>
          </div>

          <Button
            onClick={generate} disabled={busy || totalImages === 0}
            className="w-full h-12 rounded-sm bg-[#0055FF] hover:bg-[#0040CC] text-white text-base font-medium"
            data-testid="generate-button"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating {completed}/{results.length}</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" /> Generate {totalImages || ""} creative{totalImages === 1 ? "" : "s"}</>
            )}
          </Button>

          {/* Advanced — collapsed by default */}
          <div className="border-t border-zinc-100 pt-4">
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className="w-full flex items-center justify-between text-sm font-medium text-zinc-700 hover:text-zinc-900"
              data-testid="advanced-toggle"
            >
              <span className="flex items-center gap-2">
                Advanced
                {overrideCount > 0 && (
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-1.5 py-0.5 rounded-sm">
                    {overrideCount} override{overrideCount === 1 ? "" : "s"}
                  </span>
                )}
              </span>
              {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <p className="mt-1 text-xs text-zinc-400">
              Fine-tune anything — leave blank for Auto.
            </p>

            {advancedOpen && (
              <div className="mt-5 space-y-5" data-testid="advanced-panel">
                {/* Headline / CTA */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">Headline</Label>
                    <Input value={headline} onChange={(e) => setHeadline(e.target.value)}
                      placeholder="Auto" className="mt-1 rounded-sm border-zinc-200 h-9 text-sm" data-testid="headline-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-700">CTA</Label>
                    <Input value={cta} onChange={(e) => setCta(e.target.value)}
                      placeholder="Auto" className="mt-1 rounded-sm border-zinc-200 h-9 text-sm" data-testid="cta-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-zinc-700">Creative direction</Label>
                  <Textarea value={creativeDirection} onChange={(e) => setCreativeDirection(e.target.value)}
                    placeholder="Auto (or describe mood/lighting/framing)"
                    className="mt-1 rounded-sm border-zinc-200 min-h-[60px] text-sm"
                    data-testid="creative-direction-textarea" />
                </div>

                {/* Target language */}
                <div>
                  <Label className="text-xs font-medium text-zinc-700 mb-1 block">Target market language</Label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {languages.map((l) => (
                      <button
                        key={l.code} type="button" onClick={() => setLanguage(l.code)}
                        className={`px-2 py-1.5 rounded-sm border text-xs transition-all ${
                          language === l.code
                            ? "bg-zinc-900 text-white border-zinc-900"
                            : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                        }`}
                        data-testid={`lang-${l.code}`} title={l.name}
                      >
                        <div className="font-medium leading-none">{l.native}</div>
                        <div className="font-mono text-[9px] opacity-60 mt-0.5 uppercase">{l.code}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium text-zinc-700">Styles</Label>
                    <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">
                      {selectedStyles.length || "auto"}
                    </span>
                  </div>
                  <ChipPicker options={styles} selected={selectedStyles}
                    onToggle={toggle(setSelectedStyles)} testidPrefix="style-chip" compact />
                </div>

                {/* Demographic */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium text-zinc-700">Subject demographic</Label>
                    <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">
                      {selectedDemographics.length || "auto"}
                    </span>
                  </div>
                  <ChipPicker options={demographics} selected={selectedDemographics}
                    onToggle={toggle(setSelectedDemographics)} testidPrefix="demo-chip" compact />
                </div>

                {/* Ethnicity */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium text-zinc-700">Ethnicity / look hint</Label>
                    <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">
                      {selectedEthnicities.length || "auto"}
                    </span>
                  </div>
                  <ChipPicker options={ethnicities} selected={selectedEthnicities}
                    onToggle={toggle(setSelectedEthnicities)} testidPrefix="eth-chip" compact />
                </div>

                {/* Keyword color */}
                <div>
                  <Label className="text-xs font-medium text-zinc-700 mb-2 block">
                    Keyword color {keywordColor === null && <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest ml-1">auto</span>}
                  </Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => setKeywordColor(null)}
                      className={`flex items-center gap-2 px-3 h-9 rounded-sm border text-xs transition-all ${
                        keywordColor === null
                          ? "bg-zinc-900 border-zinc-900 text-white"
                          : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                      }`}
                      data-testid="kw-color-auto"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Auto
                    </button>
                    {keywordColors.map((c) => {
                      const active = keywordColor === c.key;
                      return (
                        <button key={c.key} type="button" onClick={() => setKeywordColor(c.key)} title={c.name}
                          className={`flex items-center gap-2 px-3 h-9 rounded-sm border text-xs transition-all ${
                            active
                              ? "bg-zinc-900 border-zinc-900 text-white"
                              : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                          }`}
                          data-testid={`kw-color-${c.key}`}
                        >
                          <span className="w-3.5 h-3.5 rounded-sm border border-zinc-300" style={{ backgroundColor: c.hex }} />
                          <span className="font-medium">{c.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* CTA color */}
                <div>
                  <Label className="text-xs font-medium text-zinc-700 mb-2 block">
                    CTA button color {ctaColor === null && <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest ml-1">auto</span>}
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <button type="button" onClick={() => setCtaColor(null)}
                      className={`flex items-center gap-2 px-3 h-9 rounded-sm border text-xs transition-all ${
                        ctaColor === null
                          ? "bg-zinc-900 border-zinc-900 text-white"
                          : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                      }`}
                      data-testid="cta-color-auto"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Auto
                    </button>
                    {ctaColors.map((c) => {
                      const active = ctaColor === c.key;
                      return (
                        <button key={c.key} type="button" onClick={() => setCtaColor(c.key)} title={c.name}
                          className={`flex items-center gap-2 px-3 h-9 rounded-sm border text-xs transition-all ${
                            active
                              ? "bg-zinc-900 border-zinc-900 text-white"
                              : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                          }`}
                          data-testid={`cta-color-${c.key}`}
                        >
                          <span className="w-3.5 h-3.5 rounded-full border border-zinc-300 flex-shrink-0" style={{ backgroundColor: c.hex }} />
                          <span className="font-medium truncate">{c.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Right gallery */}
      <section className="flex-1 overflow-y-auto bg-zinc-50">
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Output</div>
              <h2 className="font-heading text-2xl font-bold text-zinc-900 mt-1">Gallery</h2>
            </div>
            {okResults.length > 0 && (
              <Button onClick={downloadAll} variant="outline" className="rounded-sm border-zinc-300" data-testid="download-all-button">
                <Download className="w-4 h-4 mr-2" /> Download all ({okResults.length})
              </Button>
            )}
          </div>

          {results.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-8" data-testid="results-container">
              {grouped.map(([keyword, items]) => (
                <div key={keyword} data-testid={`group-${keyword}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-heading text-lg font-semibold text-zinc-900">{keyword}</h3>
                    <div className="flex-1 border-t border-zinc-200" />
                    <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                      {items.filter(i => i.status === "ok").length}/{items.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {items.map((r) => (
                      <ImageCard
                        key={r.key} status={r.status}
                        image={r.image || { keyword: r.keyword, style: r.style, variation_number: r.variation_number, demographic: r.demographic, ethnicity: r.ethnicity }}
                        onDownload={downloadOne} onMove={onMove} onDelete={onDelete}
                        onView={(img) => setViewImage(img)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <FolderPicker
        open={showFolderPicker} onClose={() => setShowFolderPicker(false)}
        onSelect={moveTo} currentFolderId={moveTarget?.folder_id}
      />
      <Lightbox
        open={!!viewImage} onClose={() => setViewImage(null)}
        image={viewImage} onDownload={downloadOne}
        onMove={(img) => { setViewImage(null); onMove(img); }}
        onDelete={onDelete}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-zinc-200 rounded-sm p-16 text-center bg-white" data-testid="empty-state">
      <div className="w-12 h-12 mx-auto rounded-sm bg-zinc-100 flex items-center justify-center mb-4">
        <ImageIcon className="w-6 h-6 text-zinc-400" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-lg font-semibold text-zinc-900">
        Paste keywords. Pick variations. Hit Generate.
      </h3>
      <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">
        Smart Mode picks colors, layout and demographic based on your keyword's vertical. No clicks needed.
      </p>
    </div>
  );
}
